import { useEffect, useRef, useState } from "react";
import { useSession } from "../auth/session";
import { Notice } from "../components/Feedback";
import { NavLink } from "../components/NavLink";
import { RegisterLayout } from "../experience/RegisterPrimitives";
import { billingRequest, type BillingObservation, type RecordAccess } from "./api";

export function RecordAccessNotice({ access }: { access?: RecordAccess }) {
  if (!access || access.paid && !access.workspaceErased) return null;
  if (access.trialActive) return <Notice title="Your 14-day trial"><p>You can create and change records until {new Date(access.trialEndsAt!).toLocaleString()}. No card was required and your trial will not automatically convert to a paid subscription.</p><NavLink active={false} href="/settings/billing">View trial and billing</NavLink></Notice>;
  const deadline = access.viewingEndsAt ? new Date(access.viewingEndsAt).toLocaleString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : null;
  return <Notice title={!access.recordsAvailable ? "Record access ended" : access.expiryReminder ? "Viewing and export access expires soon" : "Viewing and export access"} tone={access.expiryReminder ? "caution" : "neutral"}>
    <p>{access.recordsAvailable && deadline
      ? `You can view your records and use supported exports until ${deadline}. Editing and sending require a paid subscription.`
      : access.workspaceErased ? "Eligible workspace records were disposed of under the retention process. Billing recovery remains available; it cannot restore disposed records."
      : "Viewing and export access is closed. Access expiry alone does not delete records. Billing recovery is still available. A verified owner may request a free manual export of records still retained."}</p>
    <NavLink active={false} href="/settings/billing">View billing</NavLink>
  </Notice>;
}

// An in-app reminder uses the same server clock and verified history as the
// record guard. It grants no access and never infers a deadline in the browser.
export function SubscriptionRecordNotice() {
  const { getToken } = useSession();
  const token = useRef(getToken); token.current = getToken;
  const [access, setAccess] = useState<RecordAccess>();
  useEffect(() => {
    const controller = new AbortController();
    void billingRequest<BillingObservation>(token.current, "status", undefined, controller.signal)
      .then(value => { if (!controller.signal.aborted) setAccess(value.accessEnforced ? value.recordAccess : undefined); })
      .catch(() => { /* API guards remain authoritative when billing is unavailable. */ });
    return () => controller.abort();
  }, []);
  return access && (!access.paid || access.workspaceErased) ? <RegisterLayout><RecordAccessNotice access={access} /></RegisterLayout> : null;
}
